import { ArrowLeftRight, BellRing, ClipboardList, Layers, Mail } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { QuotePdfButton } from "@/components/admin/QuotePdfButton";
import { formatDate, formatDateTime, money } from "@/lib/format";
import { STAGE_STYLES } from "./dashboardTokens";

/** Who they are and how to reach them. */
const ContactCell = ({ row }) => (
  <TableCell className="text-sm">
    <span className="block font-medium">{row.full_name}</span>
    <span className="block text-xs text-muted-foreground">{row.company}</span>
    <span className="block text-xs text-muted-foreground">
      {row.email} · {row.phone_display}
    </span>
    {row.best_time ? (
      <span className="block text-xs text-muted-foreground">Best time: {row.best_time}</span>
    ) : null}
  </TableCell>
);

/** What they were quoted for, and whether it is a repeat. */
const ServiceCell = ({ row }) => (
  <TableCell className="text-sm">
    {row.service_label}
    <span className="mt-0.5 block max-w-[200px] truncate text-xs text-muted-foreground">
      {row.headline}
    </span>
    <span className="mt-0.5 block font-amount text-[11px] text-muted-foreground">
      {row.quote_ref}
    </span>
    {row.is_repeat ? (
      <Badge
        variant="outline"
        data-testid="admin-lead-sequence-badge"
        className="mt-1 gap-1 border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] px-1.5 py-0 text-[10px] font-semibold text-[color:var(--tt-teal-deep)]"
      >
        <Layers className="h-2.5 w-2.5" aria-hidden="true" />
        Quote {row.quote_number} of {row.quote_count}
      </Badge>
    ) : null}
  </TableCell>
);

/** Stage, whether they have gone quiet, and when the next action is due. */
const FollowUpCell = ({ row }) => (
  <TableCell className="text-xs">
    <Badge
      variant="outline"
      data-testid="admin-lead-stage-badge"
      className={`font-normal ${STAGE_STYLES[row.status] || STAGE_STYLES.new}`}
    >
      {row.status_label}
    </Badge>
    {row.gone_quiet ? (
      <span
        data-testid="admin-lead-quiet-flag"
        className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-[color:var(--tt-warn-text)]"
      >
        <BellRing className="h-3 w-3" aria-hidden="true" />
        Gone quiet · {row.days_since_activity}d
      </span>
    ) : null}
    {row.next_action_date ? (
      <span
        className={`mt-1 block text-[11px] ${
          row.next_action_overdue ? "font-semibold text-destructive" : "text-muted-foreground"
        }`}
      >
        Next: {formatDate(row.next_action_date)}
        {row.next_action_overdue ? " · overdue" : ""}
      </span>
    ) : null}
    {row.notes_count ? (
      <span className="mt-1 block text-[11px] text-muted-foreground">
        {row.notes_count} note{row.notes_count === 1 ? "" : "s"}
      </span>
    ) : null}
  </TableCell>
);

/** Quoted amount, with the first invoice spelled out for monthly work. */
const AmountCell = ({ row }) => (
  <TableCell className="whitespace-nowrap text-right">
    <span className="block font-amount text-sm font-bold">
      {money(row.quote_total)}
      {row.pricing_type === "monthly" ? (
        <span className="text-xs font-normal text-muted-foreground">/month</span>
      ) : null}
    </span>
    {row.pricing_type === "monthly" && row.first_invoice_total ? (
      <span className="block font-amount text-[11px] text-muted-foreground">
        {money(row.first_invoice_total)} first invoice
      </span>
    ) : null}
  </TableCell>
);

/** One quote, the way it has always been shown. */
const QuoteRow = ({ row, onOpenSession, onCompare }) => (
  <TableRow
    data-testid="admin-lead-row"
    onClick={() => onOpenSession(row)}
    className="cursor-pointer"
  >
    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
      {formatDateTime(row.created_at)}
    </TableCell>
    <ContactCell row={row} />
    <ServiceCell row={row} />
    <FollowUpCell row={row} />
    <TableCell className="text-xs">
      {row.next_step_label || <span className="text-muted-foreground">Not chosen</span>}
      {row.email_delivery?.sent ? (
        <span className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
          <Mail className="h-3 w-3" aria-hidden="true" />
          PDF emailed
        </span>
      ) : null}
    </TableCell>
    <AmountCell row={row} />
    {/* The row opens the session, so the download must not bubble up into
        that. */}
    <TableCell
      className="whitespace-nowrap text-right"
      onClick={(event) => event.stopPropagation()}
    >
      <QuotePdfButton quoteRef={row.quote_ref} compact />
      {row.is_repeat ? (
        <Button
          type="button"
          variant="outline"
          size="icon"
          data-testid={`admin-lead-compare-button-${row.quote_ref}`}
          aria-label={`Compare all ${row.quote_count} quotes from ${row.full_name}`}
          title={`Compare all ${row.quote_count} quotes from ${row.full_name}`}
          onClick={() => onCompare(row)}
          className="ml-1.5 h-9 w-9 shrink-0"
        >
          <ArrowLeftRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      ) : null}
    </TableCell>
  </TableRow>
);

/**
 * One outsourced-finance scoping enquiry, in the same timeline as the quotes.
 *
 * Nothing here is priced online, so the amount column says so plainly rather
 * than showing a zero that somebody might read as free.
 */
const FinanceRow = ({ row, onOpen }) => (
  <TableRow
    data-testid="admin-finance-row"
    onClick={() => onOpen(row)}
    className="cursor-pointer bg-[color:var(--tt-surface-2)]"
  >
    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
      {formatDateTime(row.created_at)}
    </TableCell>
    <ContactCell row={row} />
    <TableCell className="text-sm">
      {row.service_label}
      <span className="mt-0.5 block max-w-[200px] truncate text-xs text-muted-foreground">
        {row.support_level_label}
      </span>
      <span className="mt-0.5 block font-amount text-[11px] text-muted-foreground">
        {row.reference}
      </span>
      <Badge
        variant="outline"
        data-testid="admin-finance-type-badge"
        className="mt-1 gap-1 border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] px-1.5 py-0 text-[10px] font-semibold text-[color:var(--tt-teal-deep)]"
      >
        <ClipboardList className="h-2.5 w-2.5" aria-hidden="true" />
        Scoping enquiry
      </Badge>
    </TableCell>
    <TableCell className="text-xs">
      <Badge
        variant="outline"
        data-testid="admin-finance-status-badge"
        className={`font-normal ${STAGE_STYLES[row.status] || STAGE_STYLES.new}`}
      >
        {row.status_label}
      </Badge>
    </TableCell>
    <TableCell className="text-xs">
      Scope and call back
      {row.notification ? (
        <span className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
          <Mail className="h-3 w-3" aria-hidden="true" />
          Team alerted
        </span>
      ) : null}
    </TableCell>
    <TableCell className="whitespace-nowrap text-right text-xs text-muted-foreground">
      Not priced online
    </TableCell>
    <TableCell
      className="whitespace-nowrap text-right"
      onClick={(event) => event.stopPropagation()}
    >
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid={`admin-finance-open-button-${row.reference}`}
        onClick={() => onOpen(row)}
        className="h-9"
      >
        View brief
      </Button>
    </TableCell>
  </TableRow>
);

/**
 * Everyone who raised their hand, newest first.
 *
 * Two kinds of record share this list: quotes the calculator priced, and
 * outsourced-finance enquiries a person has to scope. They are told apart by
 * `record_type` so the team has one timeline rather than two inboxes.
 */
export const LeadsTable = ({ rows, onOpenSession, onCompare, onOpenFinance }) => (
  <Table data-testid="admin-leads-table">
    <TableHeader>
      <TableRow>
        <TableHead>Date</TableHead>
        <TableHead>Contact</TableHead>
        <TableHead>Service</TableHead>
        <TableHead>Follow-up</TableHead>
        <TableHead>Next step</TableHead>
        <TableHead className="text-right">Amount</TableHead>
        <TableHead className="text-right">Quote</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {rows.map((row) =>
        row.record_type === "finance_enquiry" ? (
          <FinanceRow key={row.reference} row={row} onOpen={onOpenFinance} />
        ) : (
          <QuoteRow
            key={row.quote_ref}
            row={row}
            onOpenSession={onOpenSession}
            onCompare={onCompare}
          />
        ),
      )}
    </TableBody>
  </Table>
);
